import { useCallback, useEffect, useState } from "react";

import { api, ApiClientError } from "../api.js";
import { DataTable } from "../components/data-table.js";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  PeriodSelector
} from "../components/state.js";
import { TrendChart } from "../components/trend-chart.js";
import {
  formatDate,
  last24HourSeries,
  shortId
} from "../format.js";
import type {
  ErrorDetail,
  ErrorList,
  Overview,
  QueryPeriod
} from "../types.js";

export function ErrorsPage({
  onUnauthorized,
  initialFingerprint
}: {
  onUnauthorized(): void;
  initialFingerprint?: string;
}) {
  const [period, setPeriod] = useState<QueryPeriod>({ period: "24h" });
  const [version, setVersion] = useState("");
  const [search, setSearch] = useState("");
  const [fatalOnly, setFatalOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState<ErrorList | null>(null);
  const [trend, setTrend] = useState<Overview | null>(null);
  const [selected, setSelected] = useState(initialFingerprint ?? "");
  const [detail, setDetail] = useState<ErrorDetail | null>(null);
  const [error, setError] = useState("");

  const loadList = useCallback(async () => {
    try {
      setError("");
      const [result, trendResult] = await Promise.all([
        api.errors(period, {
          launcherVersion: version,
          search,
          ...(fatalOnly ? { fatal: true } : {}),
          limit: 50,
          offset
        }),
        api.overview({ period: "24h" }, version)
      ]);
      setList(result);
      setTrend(trendResult);
      if (!selected && result.items[0]) {
        setSelected(result.items[0].fingerprint);
      }
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401) {
        onUnauthorized();
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [
    fatalOnly,
    offset,
    onUnauthorized,
    period,
    search,
    selected,
    version
  ]);

  useEffect(() => {
    void loadList();
    const timer = window.setInterval(() => void loadList(), 15_000);
    return () => window.clearInterval(timer);
  }, [loadList]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    void api
      .errorDetail(selected)
      .then(setDetail)
      .catch((caught: unknown) => {
        if (caught instanceof ApiClientError && caught.status === 401) {
          onUnauthorized();
        } else {
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      });
  }, [onUnauthorized, selected]);

  if (!list && !error) {
    return <LoadingState label="ГРУППИРОВКА ОШИБОК" />;
  }
  if (!list && error) {
    return <ErrorState message={error} onRetry={() => void loadList()} />;
  }
  const errorRateSeries = last24HourSeries(
    trend?.trend ?? [],
    (point) => point.error_rate
  );
  return (
    <div className="page-stack">
      <section className="toolbar toolbar--wrap">
        <PeriodSelector
          value={period}
          onChange={(value) => {
            setOffset(0);
            setPeriod(value);
          }}
        />
        <label className="field field--compact">
          <span>ВЕРСИЯ</span>
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="ВСЕ"
          />
        </label>
        <label className="field field--search">
          <span>ПОИСК</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="КОД, ОПЕРАЦИЯ, EXCEPTION"
          />
        </label>
        <label className="check-field">
          <input
            type="checkbox"
            checked={fatalOnly}
            onChange={(event) => setFatalOnly(event.target.checked)}
          />
          ТОЛЬКО FATAL
        </label>
      </section>
      {error ? (
        <div className="inline-error">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      ) : null}
      <article className="panel">
        <header className="panel__header">
          <div>
            <h2>ERROR RATE / ПОСЛЕДНИЕ 24 ЧАСА</h2>
          </div>
        </header>
        <TrendChart
          values={errorRateSeries.values}
          pointLabels={errorRateSeries.labels}
          label="ERROR RATE"
          unitLabel="ERROR RATE"
          valueFormatter={(value) =>
            `${value.toLocaleString("ru-RU", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1
            })}%`
          }
        />
      </article>
      <section className="split-view">
        <article className="panel split-view__list">
          <header className="panel__header">
            <div>
              <h2>ГРУППЫ</h2>
            </div>
            <span className="panel__meta">{list?.total ?? 0} ВСЕГО</span>
          </header>
          {list?.items.length ? (
            <>
              <DataTable
                headings={[
                  "КОД / ОПЕРАЦИЯ",
                  "ВЕРСИЯ",
                  "СЛУЧАИ",
                  "ЗАТРОНУТЫЕ ЛАУНЧЕРЫ",
                  "ПОСЛЕДНЯЯ"
                ]}
              >
                {list.items.map((item) => (
                  <tr
                    key={item.fingerprint}
                    className={`clickable-row ${
                      selected === item.fingerprint ? "is-selected" : ""
                    }`}
                    onClick={() => setSelected(item.fingerprint)}
                  >
                    <td>
                      <strong>
                        {item.fatal ? (
                          <span className="fatal-flag">FATAL</span>
                        ) : null}
                        {item.code || item.exception_type}
                      </strong>
                      <small>{item.operation}</small>
                    </td>
                    <td className="mono">{item.launcher_version}</td>
                    <td className="mono">{item.occurrence_count}</td>
                    <td className="mono">
                      {item.affected_installations}
                    </td>
                    <td className="mono">{formatDate(item.last_seen_at)}</td>
                  </tr>
                ))}
              </DataTable>
              <Pagination
                total={list.total}
                limit={list.limit}
                offset={list.offset}
                onChange={setOffset}
              />
            </>
          ) : (
            <EmptyState>ПОДХОДЯЩИХ ГРУПП НЕТ</EmptyState>
          )}
        </article>
        <article className="panel split-view__detail">
          <header className="panel__header">
            <div>
              <h2>ДЕТАЛИ ГРУППЫ</h2>
            </div>
          </header>
          {!selected ? (
            <EmptyState>ВЫБЕРИТЕ ГРУППУ СЛЕВА</EmptyState>
          ) : !detail || detail.group.fingerprint !== selected ? (
            <LoadingState label="ЧТЕНИЕ ДЕТАЛЕЙ" />
          ) : (
            <div className="error-detail">
              <div className="detail-title">
                <span>{detail.group.category}</span>
                <h3>{detail.group.code || detail.group.exception_type}</h3>
                <code title={detail.group.fingerprint}>
                  {shortId(detail.group.fingerprint, 20)}
                </code>
              </div>
              <dl className="detail-grid">
                <div>
                  <dt>ВЕРСИЯ</dt>
                  <dd>{detail.group.launcher_version}</dd>
                </div>
                <div>
                  <dt>ОПЕРАЦИЯ</dt>
                  <dd>{detail.group.operation}</dd>
                </div>
                <div>
                  <dt>СЛУЧАИ</dt>
                  <dd>{detail.group.occurrence_count}</dd>
                </div>
                <div>
                  <dt>ЗАТРОНУТЫЕ ЛАУНЧЕРЫ</dt>
                  <dd>{detail.group.affected_installations}</dd>
                </div>
                <div>
                  <dt>ПЕРВАЯ</dt>
                  <dd>{formatDate(detail.group.first_seen_at)}</dd>
                </div>
                <div>
                  <dt>ПОСЛЕДНЯЯ</dt>
                  <dd>{formatDate(detail.group.last_seen_at)}</dd>
                </div>
              </dl>
              <section className="diagnostic-block">
                <div className="diagnostic-block__label">MESSAGE</div>
                <pre>{detail.group.exemplar_message ?? "DETAIL EXPIRED"}</pre>
              </section>
              {detail.symbolicated_stack ? (
                <section className="diagnostic-block">
                  <div className="diagnostic-block__label">
                    SYMBOLICATED STACK / {detail.symbolication.resolved_frames} FRAMES
                  </div>
                  <pre>{detail.symbolicated_stack}</pre>
                </section>
              ) : null}
              <section className="diagnostic-block">
                <div className="diagnostic-block__label">RAW STACK</div>
                <pre>{detail.group.exemplar_stack ?? "DETAIL EXPIRED"}</pre>
              </section>
              <section>
                <h4 className="section-label">
                  ПОСЛЕДНИЕ ПОЯВЛЕНИЯ / {detail.recent_occurrences.length}
                </h4>
                <div className="occurrence-list">
                  {detail.recent_occurrences.slice(0, 20).map((item) => (
                    <div key={item.id}>
                      <code title={item.installation_id}>
                        {shortId(item.installation_id)}
                      </code>
                      <span>{item.installation_type}</span>
                      <time>{formatDate(item.effective_at)}</time>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
